import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Schema, OpenAPISpec } from '@/types/openapi';

interface SchemaViewerProps {
  schema: Schema | undefined;
  spec: OpenAPISpec;
  name?: string;
  required?: boolean;
  depth?: number;
}

export function SchemaViewer({ schema, spec, name, required = false, depth = 0 }: SchemaViewerProps) {
  const [expanded, setExpanded] = useState(depth < 2);

  if (!schema) {
    return <span className="text-muted-foreground">No schema defined</span>;
  }

  // Resolve $ref if present
  const resolvedSchema = resolveRef(schema, spec);

  const typeColor = getTypeColor(resolvedSchema);

  // Primitive types
  if (!resolvedSchema.type || resolvedSchema.type === 'string' || resolvedSchema.type === 'number' || resolvedSchema.type === 'integer' || resolvedSchema.type === 'boolean') {
    return (
      <div className="font-mono text-sm">
        {name && (
          <>
            <span className="text-green-600 dark:text-green-400">{name}</span>
            {required && <span className="text-red-500"> *</span>}
            <span className="text-muted-foreground">: </span>
          </>
        )}
        <span className={typeColor}>
          {resolvedSchema.enum
            ? resolvedSchema.enum.map(e => `"${e}"`).join(' | ')
            : resolvedSchema.format || resolvedSchema.type || 'any'}
        </span>
        {resolvedSchema.description && (
          <span className="text-muted-foreground ml-2">// {resolvedSchema.description}</span>
        )}
      </div>
    );
  }

  // Array type
  if (resolvedSchema.type === 'array') {
    return (
      <div className="font-mono text-sm">
        {name && (
          <>
            <span className="text-green-600 dark:text-green-400">{name}</span>
            {required && <span className="text-red-500"> *</span>}
            <span className="text-muted-foreground">: </span>
          </>
        )}
        <span className={typeColor}>array</span>
        <span className="text-muted-foreground">{'<'}</span>
        <SchemaViewer schema={resolvedSchema.items} spec={spec} depth={depth + 1} />
        <span className="text-muted-foreground">{'>'}</span>
        {resolvedSchema.description && (
          <span className="text-muted-foreground ml-2">// {resolvedSchema.description}</span>
        )}
      </div>
    );
  }

  // Object type
  if (resolvedSchema.type === 'object') {
    const properties = resolvedSchema.properties || {};
    const propertyNames = Object.keys(properties);
    const requiredFields = resolvedSchema.required || [];

    return (
      <div className="font-mono text-sm">
        {name && (
          <>
            <span className="text-green-600 dark:text-green-400">{name}</span>
            {required && <span className="text-red-500"> *</span>}
            <span className="text-muted-foreground">: </span>
          </>
        )}
        <button
          onClick={() => setExpanded(!expanded)}
          className="inline-flex items-center gap-1 hover:bg-muted rounded px-1"
        >
          <span className={typeColor}>object</span>
          <span className="text-muted-foreground">
            {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          </span>
        </button>
        {resolvedSchema.description && (
          <span className="text-muted-foreground ml-2">// {resolvedSchema.description}</span>
        )}
        {expanded && propertyNames.length > 0 && (
          <div className={cn('ml-4 border-l-2 border-muted pl-2 mt-1', depth > 0 && 'ml-2')}>
            {propertyNames.map((propName) => (
              <SchemaViewer
                key={propName}
                schema={properties[propName]}
                spec={spec}
                name={propName}
                required={requiredFields.includes(propName)}
                depth={depth + 1}
              />
            ))}
          </div>
        )}
        {expanded && propertyNames.length === 0 && (
          <div className="text-muted-foreground ml-4">{'{}'}</div>
        )}
      </div>
    );
  }

  // Fallback for unknown types
  return (
    <div className="font-mono text-sm">
      {name && (
        <>
          <span className="text-green-600 dark:text-green-400">{name}</span>
          {required && <span className="text-red-500"> *</span>}
          <span className="text-muted-foreground">: </span>
        </>
      )}
      <span className={typeColor}>{resolvedSchema.type || 'any'}</span>
    </div>
  );
}

function resolveRef(schema: Schema, spec: OpenAPISpec): Schema {
  if (!schema.$ref) {
    return schema;
  }

  // Handle $ref like "#/components/schemas/SomeModel"
  const refPath = schema.$ref.split('/').slice(1); // Remove the leading '#'
  let resolved: unknown = spec;

  for (const part of refPath) {
    if (typeof resolved === 'object' && resolved !== null && part in resolved) {
      resolved = (resolved as Record<string, unknown>)[part];
    } else {
      console.warn(`Could not resolve $ref: ${schema.$ref}`);
      return schema;
    }
  }

  if (typeof resolved === 'object' && resolved !== null) {
    return resolved as Schema;
  }

  return schema;
}

function getTypeColor(schema: Schema): string {
  const type = schema.type;

  switch (type) {
    case 'string':
      return 'text-amber-600 dark:text-amber-400';
    case 'number':
    case 'integer':
      return 'text-blue-600 dark:text-blue-400';
    case 'boolean':
      return 'text-purple-600 dark:text-purple-400';
    case 'array':
      return 'text-teal-600 dark:text-teal-400';
    case 'object':
      return 'text-blue-600 dark:text-blue-400';
    default:
      return 'text-foreground';
  }
}